import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { useHistory } from '@fs/zion-router'
import { getTogetherBackground } from './togetherAppTheme'
import { startedAtActivityEntry } from '../hooks/useActivityReturnTo'

export const EXIT_DURATION_MS = 300

const ActivityExitContext = createContext(null)

/**
 * Lets a control buried in an activity dismiss the whole activity.
 *
 * Returns null outside the provider, so callers fall back to navigating immediately.
 */
export function useActivityExit() {
  return useContext(ActivityExitContext)
}

const prefersReducedMotion = () => {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  } catch {
    return false
  }
}

const scrimCss = (background) => css`
  position: fixed;
  inset: 0;
  z-index: 0;
  background-color: ${background};
`

const sheetCss = (exiting) => css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  ${
    exiting
      ? `
  position: relative;
  z-index: 1;
  transform: translateY(100%);
  transition: transform ${EXIT_DURATION_MS}ms cubic-bezier(0.4, 0, 1, 1);
  `
      : ''
  }
`

/**
 * Slides the activity down off the screen before it closes, revealing a TWA theme-matching scrim behind it.
 *
 * Honors prefers-reduced-motion by skipping straight to the caller's exit.
 */
export default function ActivityExitProvider({ children }) {
  const [exiting, setExiting] = useState(false)
  const onExitedRef = useRef(null)
  const history = useHistory()

  // How many entries the activity has pushed since it opened, counted from the router to prevent overstating
  // the depth after drilling in and coming back.
  //
  // Only meaningful if this document saw the whole visit. On a reload deeper in the activity, the depth is
  // reported as unknown instead, and the caller falls back to navigating to returnTo outright, which is slower
  // but gets the user back to the right place. Whether this document is the entry is decided by
  // captureActivityEntry at document start, since returnTo alone rides along on every URL the activity pushes.
  const depthRef = useRef(0)
  const depthIsKnown = useRef(startedAtActivityEntry())

  // Whether the user has moved around inside the activity since this document loaded.
  // Used to prevent flashing loading label again when going into a category and back out.
  const hasNavigatedRef = useRef(false)

  useEffect(() => {
    if (!history?.listen) return undefined

    return history.listen((location, action) => {
      if (action === 'PUSH') depthRef.current += 1
      else if (action === 'POP') depthRef.current = Math.max(0, depthRef.current - 1)
      else return

      hasNavigatedRef.current = true
    })
  }, [history])

  // Stand the sheet back up if we are still here after an exit.
  useEffect(() => {
    if (!exiting || !history?.listen) return undefined

    return history.listen(() => setExiting(false))
  }, [exiting, history])

  const getExitDepth = useCallback(() => (depthIsKnown.current ? depthRef.current : null), [])
  const hasNavigatedWithin = useCallback(() => hasNavigatedRef.current, [])

  const startExit = useCallback((onExited) => {
    if (prefersReducedMotion()) {
      onExited?.()
      return
    }

    onExitedRef.current = onExited
    setExiting(true)
  }, [])

  const handleTransitionEnd = useCallback((event) => {
    if (event.currentTarget !== event.target) return
    if (event.propertyName && event.propertyName !== 'transform') return

    const onExited = onExitedRef.current
    onExitedRef.current = null
    onExited?.()
  }, [])

  const value = useMemo(
    () => ({ startExit, exiting, getExitDepth, hasNavigatedWithin }),
    [startExit, exiting, getExitDepth, hasNavigatedWithin]
  )

  return (
    <ActivityExitContext.Provider value={value}>
      {exiting && <div css={scrimCss(getTogetherBackground())} aria-hidden="true" data-testid="activity-exit-scrim" />}
      <div
        css={sheetCss(exiting)}
        onTransitionEnd={exiting ? handleTransitionEnd : undefined}
        inert={exiting ? '' : undefined}
        data-testid="activity-exit-sheet"
      >
        {children}
      </div>
    </ActivityExitContext.Provider>
  )
}

ActivityExitProvider.propTypes = {
  children: PropTypes.node,
}
